import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./EditTechnicalAchievements.css";
import {
  scrollToSpecificContentAction,
  setProfileState,
} from "../../../actions/MyTechProfile/myTechProfileActions";
import { Button, TextField, IconButton, Popover } from "@mui/material";
import { Add, Info as InfoIcon } from "@mui/icons-material";

const COOLDOWN_MINUTES = 60;
const COOLDOWN_MS = COOLDOWN_MINUTES * 60 * 1000;

const EditTechnicalAchievements = () => {
  const { profile } = useSelector((state) => state.editMyTechProfileStore);
  const dispatch = useDispatch();

  // Local state to manage form data
  const [formData, setFormData] = useState(
    profile?.technicalAchievements || { platforms: [] }
  );
  // State for Popover anchor (info icon)
  const [anchorEl, setAnchorEl] = useState(null);

  // --- Cooldown State ---
  const [githubCooldown, setGithubCooldown] = useState(null);
  const [cooldownError, setCooldownError] = useState("");
  const [isGithubCooldownActive, setIsGithubCooldownActive] = useState(false);
  const [hasEverSaved, setHasEverSaved] = useState(false);

  // State to manage the visibility of each section
  const [showGithub, setShowGithub] = useState(
    !!profile.technicalAchievements?.platforms?.some((p) => p.id === "github")
  );
  const [showLeetcode, setShowLeetcode] = useState(
    !!profile.technicalAchievements?.platforms?.some((p) => p.id === "leetcode")
  );
  const [showGfg, setShowGfg] = useState(
    !!profile.technicalAchievements?.platforms?.some((p) => p.id === "gfg")
  );

  useEffect(() => {
    const lastChangeTimestamp = localStorage.getItem(
      "githubUsernameLastChange"
    );
    if (lastChangeTimestamp) {
      setHasEverSaved(true);
      const cooldownEndTime = parseInt(lastChangeTimestamp, 10) + COOLDOWN_MS;
      const now = new Date().getTime();

      if (now < cooldownEndTime) {
        setGithubCooldown(cooldownEndTime);
        const remainingMinutes = Math.ceil((cooldownEndTime - now) / 60000);
        setCooldownError(
          `Due to high demand, please try again in about ${remainingMinutes} minute${
            remainingMinutes > 1 ? "s" : ""
          }.`
        );
        setIsGithubCooldownActive(true);

        const remainingTime = cooldownEndTime - now;
        setTimeout(() => {
          setGithubCooldown(null);
          setCooldownError("");
          setIsGithubCooldownActive(false);
        }, remainingTime);
      }
    }

    // Sync local state if Redux profile changes
    const achievements = profile.technicalAchievements || { platforms: [] };
    setFormData(achievements);
    setShowGithub(!!achievements.platforms?.some((p) => p.id === "github"));
    setShowLeetcode(!!achievements.platforms?.some((p) => p.id === "leetcode"));
    setShowGfg(!!achievements.platforms?.some((p) => p.id === "gfg"));
  }, []); // Note: Dependency array is empty to run only on mount

  // Generic handler for all platform data
  const handlePlatformChange = (
    platformId,
    field,
    value,
    isStat = false,
    isBadge = false,
    badgeIndex,
    badgeField = "url"
  ) => {
    setFormData((prev) => ({
      ...prev,
      platforms: prev.platforms.map((p) => {
        if (p.id === platformId) {
          if (isStat) {
            return {
              ...p,
              stats: { ...p.stats, [field]: parseInt(value, 10) || 0 },
            };
          }
          if (isBadge) {
            const newBadges = [...(p.badges || [])];
            if (!newBadges[badgeIndex]) {
              newBadges[badgeIndex] = {
                id: `badge${badgeIndex + 1}`,
                url: "",
                alt: "",
              };
            }
            newBadges[badgeIndex] = {
              ...newBadges[badgeIndex],
              [badgeField]: value,
            };
            return { ...p, badges: newBadges };
          }
          const updatedPlatform = { ...p, [field]: value };

          // *** ADDED LOGIC ***
          // If the platform is GitHub and the username is being changed,
          // update the profileLink as well.
          if (platformId === "github" && field === "username") {
            updatedPlatform.profileLink = `https://github.com/${value}`;
          }

          return updatedPlatform;
        }
        return p;
      }),
    }));
  };

  const handleSave = (activeTab) => {
    if (activeTab === "github") {
      const now = new Date().getTime();

      if (githubCooldown && now < githubCooldown) {
        const remainingMinutes = Math.ceil((githubCooldown - now) / 60000);
        setCooldownError(
          `Due to high demand, please try again in about ${remainingMinutes} minute${
            remainingMinutes > 1 ? "s" : ""
          }.`
        );
        return;
      }

      localStorage.setItem("githubUsernameLastChange", now.toString());
      setHasEverSaved(true);
      setGithubCooldown(now + COOLDOWN_MS);
      const remainingMinutes = Math.ceil(COOLDOWN_MS / 60000);
      setCooldownError(
        `Due to high demand, please try again in about ${remainingMinutes} minute${
          remainingMinutes > 1 ? "s" : ""
        }.`
      );
      setIsGithubCooldownActive(true);

      setTimeout(() => {
        setGithubCooldown(null);
        setCooldownError("");
        setIsGithubCooldownActive(false);
      }, COOLDOWN_MS);
    }

    dispatch(
      setProfileState({
        ...profile,
        technicalAchievements: { ...formData, currentActiveTab: activeTab },
      })
    );
    dispatch(scrollToSpecificContentAction("achievements"));
  };

  // --- Section Toggle Handlers ---
  const handleRemoveSection = (sectionId) => {
    if (sectionId === "github") setShowGithub(false);
    if (sectionId === "leetcode") setShowLeetcode(false);
    if (sectionId === "gfg") setShowGfg(false);

    const updatedPlatforms = formData.platforms.filter(
      (p) => p.id !== sectionId
    );

    setFormData((prev) => ({
      ...prev,
      platforms: updatedPlatforms,
    }));
    dispatch(
      setProfileState({
        ...profile,
        technicalAchievements: {
          ...profile.technicalAchievements,
          platforms: updatedPlatforms,
        },
      })
    );
    dispatch(scrollToSpecificContentAction("achievements"));
  };

  const handleAddSection = (sectionId) => {
    let newPlatform;
    if (sectionId === "github") {
      setShowGithub(true);
      newPlatform = {
        id: "github",
        platformName: "GitHub",
        username: "your-username",
        stats: {
          totalRepos: 42,
          languages: [
            { name: "JavaScript", value: 65.5 },
            { name: "TypeScript", value: 25.2 },
            { name: "HTML", value: 5.1 },
            { name: "CSS", value: 4.2 },
          ],
          lastFetched: "2025-10-07T00:00:00.000Z",
        }, // GitHub might have different stats
      };
    } else {
      if (sectionId === "leetcode") setShowLeetcode(true);
      if (sectionId === "gfg") setShowGfg(true);

      newPlatform = {
        id: sectionId,
        platformName: sectionId === "leetcode" ? "LeetCode" : "GeeksforGeeks",
        username: "your-username",
        stats: { easy: 0, medium: 0, hard: 0 },
        badges: sectionId === "leetcode" ? ["", "", ""] : undefined,
      };
    }

    const updatedPlatforms =
      sectionId === "github"
        ? [
            newPlatform,
            ...(formData.platforms || []).filter((p) => p.id !== "github"),
          ]
        : [...(formData.platforms || []), newPlatform];
    setFormData((prev) => ({
      ...prev,
      platforms: updatedPlatforms,
    }));
    dispatch(
      setProfileState({
        ...profile,
        technicalAchievements: {
          ...profile.technicalAchievements,
          currentActiveTab: sectionId,
          platforms: updatedPlatforms,
        },
      })
    );
    dispatch(scrollToSpecificContentAction("achievements"));
  };

  // --- Popover Handlers ---
  const handlePopoverOpen = (event) => setAnchorEl(event.currentTarget);
  const handlePopoverClose = () => setAnchorEl(null);
  const isPopoverOpen = Boolean(anchorEl);

  const githubData = formData?.platforms?.find((p) => p.id === "github");
  const leetcodeData = formData?.platforms?.find((p) => p.id === "leetcode");
  const gfgData = formData?.platforms?.find((p) => p.id === "gfg");

  return (
    <div className="edittechnicalachievements__form">
      {/* --- GitHub Section --- */}
      {showGithub && githubData ? (
        <div className="edittechnicalachievements__section">
          <h4 className="edittechnicalachievements__subtitle">GitHub</h4>
          {!hasEverSaved && (
            <div className="edittechnicalachievements__initial-note">
              <InfoIcon fontSize="small" style={{ marginRight: "8px" }} />
              <span>
                Due to high demand, you can change your username on an hourly
                basis. Please add your own GitHub username.
              </span>
            </div>
          )}
          <label className="edittechnicalachievements__label">
            GitHub Username
          </label>
          <TextField
            type="text"
            name="githubUsername"
            value={githubData.username || ""}
            onChange={(e) =>
              handlePlatformChange("github", "username", e.target.value)
            }
            onFocus={() => {
              dispatch(
                setProfileState({
                  ...profile,
                  technicalAchievements: {
                    ...profile.technicalAchievements,
                    currentActiveTab: "github",
                  },
                })
              );
              dispatch(scrollToSpecificContentAction("achievements"));
            }}
            placeholder="e.g., your-github-username"
            variant="outlined"
            size="small"
            fullWidth
            disabled={isGithubCooldownActive}
            error={isGithubCooldownActive}
            helperText={isGithubCooldownActive ? cooldownError : ""}
          />
          <div className="edittechnicalachievements__button-group">
            <Button
              onClick={() => handleSave("github")}
              className="edittechnicalachievements__button"
              disabled={isGithubCooldownActive}
            >
              Save
            </Button>
            <Button
              onClick={() => handleRemoveSection("github")}
              className="edittechnicalachievements__button edittechnicalachievements__button--remove"
              disabled={isGithubCooldownActive}
            >
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <div className="edittechnicalachievements__add-section">
          <Button
            onClick={() => handleAddSection("github")}
            className="edittechnicalachievements__button--add"
            startIcon={<Add />}
          >
            Add GitHub Details
          </Button>
        </div>
      )}

      {/* --- LeetCode Section --- */}
      {showLeetcode && leetcodeData ? (
        <div className="edittechnicalachievements__section">
          <h4 className="edittechnicalachievements__subtitle">LeetCode</h4>
          <label className="edittechnicalachievements__label">
            LeetCode Username
          </label>
          <TextField
            type="text"
            value={leetcodeData.username}
            onChange={(e) =>
              handlePlatformChange("leetcode", "username", e.target.value)
            }
            onFocus={() => {
              dispatch(
                setProfileState({
                  ...profile,
                  technicalAchievements: {
                    ...profile.technicalAchievements,
                    currentActiveTab: "leetcode",
                  },
                })
              );
              dispatch(scrollToSpecificContentAction("achievements"));
            }}
            placeholder="your-leetcode-username"
            variant="outlined"
            size="small"
            fullWidth
          />
          <div className="edittechnicalachievements__stats-container">
            <div className="edittechnicalachievements__stats-row">
              <div className="edittechnicalachievements__stats-item">
                <label>Easy:</label>
                <TextField
                  value={leetcodeData.stats.easy}
                  onChange={(e) =>
                    handlePlatformChange(
                      "leetcode",
                      "easy",
                      e.target.value,
                      true
                    )
                  }
                  onFocus={() => {
                    dispatch(
                      setProfileState({
                        ...profile,
                        technicalAchievements: {
                          ...profile.technicalAchievements,
                          currentActiveTab: "leetcode",
                        },
                      })
                    );
                    dispatch(scrollToSpecificContentAction("achievements"));
                  }}
                  variant="outlined"
                  size="small"
                />
              </div>
              <div className="edittechnicalachievements__stats-item">
                <label>Medium:</label>
                <TextField
                  value={leetcodeData.stats.medium}
                  onChange={(e) =>
                    handlePlatformChange(
                      "leetcode",
                      "medium",
                      e.target.value,
                      true
                    )
                  }
                  onFocus={() => {
                    dispatch(
                      setProfileState({
                        ...profile,
                        technicalAchievements: {
                          ...profile.technicalAchievements,
                          currentActiveTab: "leetcode",
                        },
                      })
                    );
                    dispatch(scrollToSpecificContentAction("achievements"));
                  }}
                  variant="outlined"
                  size="small"
                />
              </div>
            </div>
            <div className="edittechnicalachievements__stats-row--centered">
              <div className="edittechnicalachievements__stats-item">
                <label>Hard:</label>
                <TextField
                  value={leetcodeData.stats.hard}
                  onChange={(e) =>
                    handlePlatformChange(
                      "leetcode",
                      "hard",
                      e.target.value,
                      true
                    )
                  }
                  onFocus={() => {
                    dispatch(
                      setProfileState({
                        ...profile,
                        technicalAchievements: {
                          ...profile.technicalAchievements,
                          currentActiveTab: "leetcode",
                        },
                      })
                    );
                    dispatch(scrollToSpecificContentAction("achievements"));
                  }}
                  variant="outlined"
                  size="small"
                />
              </div>
            </div>
          </div>
          <div className="edittechnicalachievements__badges-section">
            <h5 className="edittechnicalachievements__badges-title">
              Add your badges
              <IconButton
                size="small"
                onClick={isPopoverOpen ? handlePopoverClose : handlePopoverOpen}
                aria-owns={isPopoverOpen ? "mouse-over-popover" : undefined}
                aria-haspopup="true"
                className="edittechnicalachievements__info-icon"
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </h5>
            <p className="edittechnicalachievements__badges-note">
              You can add a maximum of 3 badges. Click the info icon to see how
              to get the badge link.
            </p>
            <Popover
              id="mouse-over-popover"
              open={isPopoverOpen}
              anchorEl={anchorEl}
              onClose={handlePopoverClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              disableRestoreFocus
              onFocus={() => {
                dispatch(
                  setProfileState({
                    ...profile,
                    technicalAchievements: {
                      ...profile.technicalAchievements,
                      currentActiveTab: "leetcode",
                    },
                  })
                );
                dispatch(scrollToSpecificContentAction("achievements"));
              }}
            >
              <div className="edittechnicalachievements__popover-content">
                <video
                  src="/videos/leetcode-badge-tutorial.mp4"
                  autoPlay
                  muted
                  loop
                  className="edittechnicalachievements__popover-video"
                />
                <ol className="edittechnicalachievements__popover-steps">
                  <li>Go to your LeetCode profile page.</li>
                  <li>Find the badge you want to display.</li>
                  <li>
                    Right-click on the badge image and select "Copy Image
                    Address".
                  </li>
                  <li>Paste the copied link into one of the fields below.</li>
                </ol>
              </div>
            </Popover>
            {["", "", ""].map((_, index) => (
              <TextField
                key={index}
                type="text"
                placeholder={`Badge Link ${index + 1}`}
                value={leetcodeData?.badges?.[index]?.url || ""}
                onChange={(e) =>
                  handlePlatformChange(
                    "leetcode",
                    "badges",
                    e.target.value,
                    false,
                    true,
                    index
                  )
                }
                onFocus={() => {
                  dispatch(
                    setProfileState({
                      ...profile,
                      technicalAchievements: {
                        ...profile.technicalAchievements,
                        currentActiveTab: "leetcode",
                      },
                    })
                  );
                  dispatch(scrollToSpecificContentAction("achievements"));
                }}
                variant="outlined"
                size="small"
                fullWidth
                className="edittechnicalachievements__badge-input"
              />
            ))}
          </div>
          <div className="edittechnicalachievements__button-group">
            <Button
              onClick={() => handleSave("leetcode")}
              className="edittechnicalachievements__button"
            >
              Save
            </Button>
            <Button
              onClick={() => handleRemoveSection("leetcode")}
              className="edittechnicalachievements__button edittechnicalachievements__button--remove"
            >
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <div className="edittechnicalachievements__add-section">
          <Button
            onClick={() => handleAddSection("leetcode")}
            className="edittechnicalachievements__button--add"
            startIcon={<Add />}
          >
            Add LeetCode Details
          </Button>
        </div>
      )}

      {/* --- GeeksforGeeks Section --- */}
      {showGfg && gfgData ? (
        <div className="edittechnicalachievements__section">
          <h4 className="edittechnicalachievements__subtitle">GeeksforGeeks</h4>
          <label className="edittechnicalachievements__label">
            GFG Username
          </label>
          <TextField
            type="text"
            value={gfgData.username}
            onChange={(e) =>
              handlePlatformChange("gfg", "username", e.target.value)
            }
            onFocus={() => {
              dispatch(
                setProfileState({
                  ...profile,
                  technicalAchievements: {
                    ...profile.technicalAchievements,
                    currentActiveTab: "gfg",
                  },
                })
              );
              dispatch(scrollToSpecificContentAction("achievements"));
            }}
            placeholder="your-gfg-username"
            variant="outlined"
            size="small"
            fullWidth
          />
          <div className="edittechnicalachievements__stats-container">
            <div className="edittechnicalachievements__stats-row">
              <div className="edittechnicalachievements__stats-item">
                <label>Easy:</label>
                <TextField
                  value={gfgData.stats.easy}
                  onChange={(e) =>
                    handlePlatformChange("gfg", "easy", e.target.value, true)
                  }
                  variant="outlined"
                  size="small"
                  onFocus={() => {
                    dispatch(
                      setProfileState({
                        ...profile,
                        technicalAchievements: {
                          ...profile.technicalAchievements,
                          currentActiveTab: "gfg",
                        },
                      })
                    );
                    dispatch(scrollToSpecificContentAction("achievements"));
                  }}
                />
              </div>
              <div className="edittechnicalachievements__stats-item">
                <label>Medium:</label>
                <TextField
                  value={gfgData.stats.medium}
                  onChange={(e) =>
                    handlePlatformChange("gfg", "medium", e.target.value, true)
                  }
                  variant="outlined"
                  size="small"
                  onFocus={() => {
                    dispatch(
                      setProfileState({
                        ...profile,
                        technicalAchievements: {
                          ...profile.technicalAchievements,
                          currentActiveTab: "gfg",
                        },
                      })
                    );
                    dispatch(scrollToSpecificContentAction("achievements"));
                  }}
                />
              </div>
            </div>
            <div className="edittechnicalachievements__stats-row--centered">
              <div className="edittechnicalachievements__stats-item">
                <label>Hard:</label>
                <TextField
                  value={gfgData.stats.hard}
                  onChange={(e) =>
                    handlePlatformChange("gfg", "hard", e.target.value, true)
                  }
                  variant="outlined"
                  size="small"
                  onFocus={() => {
                    dispatch(
                      setProfileState({
                        ...profile,
                        technicalAchievements: {
                          ...profile.technicalAchievements,
                          currentActiveTab: "gfg",
                        },
                      })
                    );
                    dispatch(scrollToSpecificContentAction("achievements"));
                  }}
                />
              </div>
            </div>
          </div>
          <div className="edittechnicalachievements__button-group">
            <Button
              onClick={() => handleSave("gfg")}
              className="edittechnicalachievements__button"
            >
              Save
            </Button>
            <Button
              onClick={() => handleRemoveSection("gfg")}
              className="edittechnicalachievements__button edittechnicalachievements__button--remove"
            >
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <div className="edittechnicalachievements__add-section">
          <Button
            onClick={() => handleAddSection("gfg")}
            className="edittechnicalachievements__button--add"
            startIcon={<Add />}
          >
            Add GeeksforGeeks Details
          </Button>
        </div>
      )}
    </div>
  );
};

export default EditTechnicalAchievements;
